import { GameEvents } from '../GameEvents';
import type EventBus from '../EventBus';
import type { UiState } from '../GameManager';
import getLogger from '../logger';

const logger = getLogger('SolutionReplayHandler');

interface SolutionReplaySlice {
  currentStepIndex: number;
  totalSteps: number;
  playerSolution: any[];
  canStepBackward: boolean;
  canStepForward: boolean;
  replayCompleted: boolean;
}

export default class SolutionReplayHandler {
  private state: UiState;
  private slice: SolutionReplaySlice;
  private eventBus: EventBus;
  private pendingReplayStart: boolean = false;
  private restartingInReplayMode: boolean = false;
  private pendingSolution: any[] | null = null;

  constructor(state: UiState, slice: SolutionReplaySlice, eventBus: EventBus) {
    this.state = state;
    this.slice = slice;
    this.eventBus = eventBus;

    // Lifecycle
    this.eventBus.on(GameEvents.LEVEL_STARTED, () => this._onLevelStarted());

    // Replay controls
    this.eventBus.on(GameEvents.START_SOLUTION_REPLAY, (payload?: any) => this._onStartReplay(payload));
    this.eventBus.on(GameEvents.EXIT_SOLUTION_REPLAY, () => this._onExitReplay());
    this.eventBus.on(GameEvents.SOLUTION_REPLAY_STEP_FORWARD, () => this._onStepForward());
    this.eventBus.on(GameEvents.SOLUTION_REPLAY_STEP_BACKWARD, () => this._onStepBackward());

    // Consume win/loss during replay to prevent progress recording
    this.eventBus.on(GameEvents.LEVEL_WON, () => this._consumeIfActive());
    this.eventBus.on(GameEvents.LEVEL_LOST, () => this._consumeIfActive());

    // Input blocking (dispatch events - can consume)
    this.eventBus.on(GameEvents.PLAYER_TAPPED_HEX, () => this._blockIfActive());
    this.eventBus.on(GameEvents.SET_ACTION_MODE, () => this._blockIfActive());
    this.eventBus.on(GameEvents.UNDO_LAST_MOVE, () => this._blockIfActive());
    this.eventBus.on(GameEvents.RESTART_GAME, () => this._onRestart());
    this.eventBus.on(GameEvents.RETURN_TO_HOME, () => this._onReturnToHome());
  }

  private _onStartReplay(payload?: { solution?: any[]; levelKey?: string }) {
    const solution = payload?.solution || this.state.gameplay.lastCompletedLevelSolution;

    if (!solution || solution.length === 0) {
      logger.warn('No solution to replay');
      return;
    }

    logger.info('Starting solution replay');

    // Store the solution to use when the level starts
    this.pendingSolution = solution;
    this.pendingReplayStart = true;

    // Load a different level or restart current one
    if (payload?.levelKey && payload.levelKey !== this.state.gameplay.currentLevelKey) {
      this.eventBus.emit(GameEvents.PLAY_LEVEL, { levelKey: payload.levelKey });
    } else {
      this.eventBus.emit(GameEvents.REPLAY_CURRENT_LEVEL);
    }
  }

  private _onLevelStarted() {
    if (this.pendingReplayStart || this.restartingInReplayMode) {
      const playerSolution = this.restartingInReplayMode
        ? this.slice.playerSolution  // Keep current solution when restarting in replay mode
        : this.pendingSolution || [];

      // Enter/re-enter replay mode
      this.state.activePlayMode = 'replay';
      this.slice.playerSolution = playerSolution;
      this.slice.totalSteps = playerSolution.length;
      this.slice.currentStepIndex = 0;
      this.slice.replayCompleted = false;
      this._updateDerivedState();

      this.pendingReplayStart = false;
      this.restartingInReplayMode = false;
      this.pendingSolution = null;
      logger.info(`Replay mode active with ${playerSolution.length} steps`);
    }
  }

  private _onExitReplay() {
    logger.info('Exiting solution replay');
    this._reset();

    // Restart level to return to normal play
    this.eventBus.emit(GameEvents.REPLAY_CURRENT_LEVEL);
  }

  private _onStepForward() {
    if (this.state.activePlayMode !== 'replay') return;
    if (!this.slice.canStepForward) return;

    const relativeAction = this.slice.playerSolution[this.slice.currentStepIndex];

    // Emit relative action - LevelManager will convert to absolute coords
    this.eventBus.emit(GameEvents.REPLAY_SOLUTION_ACTION, relativeAction);

    // Advance
    this.slice.currentStepIndex++;
    this._updateDerivedState();
  }

  private _onStepBackward() {
    if (this.state.activePlayMode !== 'replay') return;
    if (!this.slice.canStepBackward) return;

    // Undo (emit not dispatch to avoid self-consumption)
    this.eventBus.emit(GameEvents.UNDO_LAST_MOVE);

    // Go back
    this.slice.currentStepIndex--;
    this._updateDerivedState();
  }

  private _consumeIfActive(): boolean | void {
    if (this.state.activePlayMode === 'replay') {
      logger.info('Consuming level outcome during replay');
      this.slice.replayCompleted = true;
      return true; // Consume event
    }
  }

  private _blockIfActive(): boolean | void {
    if (this.state.activePlayMode === 'replay') {
      logger.debug('Blocking player input during replay');
      return true; // Consume event
    }
  }

  private _onRestart(): boolean | void {
    if (this.state.activePlayMode === 'replay') {
      // Restart from beginning of replay (reset to step 0)
      logger.info('Restarting replay from beginning');
      this.restartingInReplayMode = true;
      this.eventBus.emit(GameEvents.REPLAY_CURRENT_LEVEL);
      return true; // Consume to prevent normal restart
    }
  }

  private _onReturnToHome() {
    // Exit replay mode when returning to home
    if (this.state.activePlayMode === 'replay') {
      logger.info('Exiting replay mode (returning to home)');
      this._reset();
    }
  }

  private _reset() {
    this.state.activePlayMode = 'normal';
    this.slice.currentStepIndex = 0;
    this.slice.totalSteps = 0;
    this.slice.playerSolution = [];
    this.slice.replayCompleted = false;
    this.pendingReplayStart = false;
    this.restartingInReplayMode = false;
    this.pendingSolution = null;
    this._updateDerivedState();
  }

  private _updateDerivedState() {
    this.slice.canStepBackward = this.slice.currentStepIndex > 0;
    this.slice.canStepForward = this.slice.currentStepIndex < this.slice.totalSteps;
  }
}
